import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { TextArea } from "@/components/design/TextField";
import {
  SystemSettingsDocument,
  UpdateSystemSettingsDocument,
} from "@/graphql/generated/operations";
import { usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import { SettingsCard } from "./SettingsCard";

// Platform-wide recruiter base prompt. A match can add a later supplement,
// but never has to duplicate this text. Saving only patches this one
// system-settings field; Genesis merges the patch with branding and uploads.
export function HrPromptPanel() {
  const intl = useIntl();
  const { data, loading, refetch } = useQuery(SystemSettingsDocument);
  const [updateSettings] = useMutation(UpdateSystemSettingsDocument);
  const [promptOverride, setPromptOverride] = useState<string | null>();
  const initialized = useRef(false);

  const rawStoredPrompt = data?.systemSettings?.candidateAssessmentPrompt;
  const storedPrompt = rawStoredPrompt?.trim() ? rawStoredPrompt : null;
  const defaultPrompt = data?.defaultCandidateAssessmentPrompt ?? "";
  useEffect(() => {
    if (initialized.current || loading || !data) return;
    initialized.current = true;
    setPromptOverride(storedPrompt);
  }, [data, loading, storedPrompt]);

  const resolvedPromptOverride = promptOverride === undefined ? storedPrompt : promptOverride;
  const effectivePrompt = resolvedPromptOverride ?? defaultPrompt;
  const dirty = promptOverride !== undefined && promptOverride !== storedPrompt;

  usePendingScope("hr-prompt", {
    dirty,
    save: async () => {
      const normalized = resolvedPromptOverride?.trim() || null;
      try {
        await updateSettings({
          variables: {
            input: { data: { candidateAssessmentPrompt: normalized } },
          },
        });
        setPromptOverride(normalized);
        await refetch();
      } catch (error) {
        throw new LocalizedError(userErrorMessage(intl, error), { cause: error });
      }
    },
    discard: () => setPromptOverride(storedPrompt),
  });

  return (
    <SettingsCard
      title={intl.formatMessage({
        id: "adminSettings.hrPrompt.title",
        defaultMessage: "HR-Prompt",
      })}
      sub={intl.formatMessage({
        id: "adminSettings.hrPrompt.sub",
        defaultMessage:
          "Zentrale Recruiter-Anweisung für alle Prüf-Matches. Match-spezifische Ergänzungen werden danach angefügt.",
      })}
    >
      <TextArea
        mono
        value={effectivePrompt}
        onChange={(event) => setPromptOverride(event.target.value)}
        disabled={loading}
        rows={18}
        aria-label={intl.formatMessage({
          id: "adminSettings.hrPrompt.label",
          defaultMessage: "Globaler HR-Basis-Prompt",
        })}
      />
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-[11px] leading-relaxed text-ink/55">
          {intl.formatMessage({
            id: "adminSettings.hrPrompt.hint",
            defaultMessage:
              "Der Platzhalter '{{responseLanguage}}' wird automatisch ersetzt. Eine Match-Ergänzung steht im kombinierten Prompt zuletzt und hat bei Konflikten Vorrang.",
          })}
        </p>
        {resolvedPromptOverride !== null ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPromptOverride(null)}
          >
            {intl.formatMessage({
              id: "adminSettings.hrPrompt.reset",
              defaultMessage: "Genesis-Standard verwenden",
            })}
          </Button>
        ) : null}
      </div>
      {resolvedPromptOverride === null ? (
        <div className="mt-2 text-[10px] text-ink/45">
          {intl.formatMessage({
            id: "adminSettings.hrPrompt.defaultActive",
            defaultMessage: "Aktuell ist der Genesis-Standard aktiv.",
          })}
        </div>
      ) : null}
    </SettingsCard>
  );
}
